import { useRef, type ReactNode } from "react";
import {
  Button,
  Icons,
  PocketRow,
  PocketRowWrapScope,
  type StatusTone,
} from "@/components/ui";
import type { IconName } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { cn } from "@/lib/cn";
import "./toast-motion.css";
import { ToastMoreMenu, type ToastMenuItem } from "./toast-more-menu";

export interface ToastPillAction {
  id: string;
  label: string;
  /** Overrides the default `toast-action-${id}` test id. */
  testId?: string;
  icon?: IconName;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * The pill/card use the "menu" surface role's own radius (24px, renders as
 * a full capsule on the 46px-tall pill) rather than a local override — the
 * native glass backing (native_glass_style.rs) draws that role at a fixed
 * radius regardless of CSS, so a component-local radius can only mismatch
 * it on desktop. See surface-contract.test.ts "keeps structural surface
 * radius owned by the role contract".
 */
export const toastShellClassName = cn(
  surfaceRoleClassName("menu"),
  "pointer-events-auto border border-glass-stroke shadow-glass backdrop-blur-md",
);
export const toastShellAttributes = surfaceRoleAttributes("menu", {
  nativeLevel: "root",
  nativeGroup: "menus",
});

/** The 28px round logo slot on the soft fill (compact surface standard §6). */
export function ToastLogoTile({
  children,
  testId,
  className,
}: {
  children: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-full bg-[var(--color-surface-selected)]",
        className,
      )}
      data-testid={testId}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

/** Text actions are neutral buttons, icon actions round icon buttons (§6). */
export function ToastActionButton({ action }: { action: ToastPillAction }) {
  const Icon = action.icon ? Icons[action.icon] : null;
  return (
    <Button
      type="button"
      variant={Icon ? "ghost" : "secondary"}
      size={Icon ? "iconSm" : "sm"}
      disabled={action.disabled}
      aria-label={action.label}
      title={action.label}
      data-testid={action.testId ?? `toast-action-${action.id}`}
      data-interactive="true"
      onClick={(event) => {
        event.stopPropagation();
        action.onClick();
      }}
    >
      {Icon ? <Icon className="h-4 w-4" /> : action.label}
    </Button>
  );
}

export interface ToastPillProps {
  toastKey: string;
  state: string;
  logo: ReactNode;
  title: string;
  detail: string;
  /** Lines the detail may take while collapsed: one unless a notice needs two to read whole. */
  detailLines?: 1 | 2;
  detailIcon?: IconName;
  dot: StatusTone | null;
  actions: ToastPillAction[];
  /** Keeps the actions visible without hover (a finished native worker's follow-up). */
  actionsPinned?: boolean;
  /** Pinned text actions get their own line under the text, which keeps one
   *  line: for a label too long to share the row with it. */
  actionsBelow?: boolean;
  menuItems: ToastMenuItem[];
  onBodyClick?: () => void;
  onHoverChange?: (hovered: boolean) => void;
  onMenuOpenChange?: (open: boolean) => void;
  /** Render as a full-width row inside another glass surface. */
  embedded?: boolean;
}

/**
 * One work or system toast as a PocketRow (compact surface standard §8): the
 * 46px-high capsule (`--denker-toast-width` wide) on the menu material at the screen edge, or a plain row
 * inside the tray (`embedded`). Title 13px/500, a plain second line, and the
 * status dot at the end; hover, keyboard focus or an open ⋮ menu show the
 * actions in the dot's place (PocketRow's hover actions).
 */
export function ToastPill({
  toastKey,
  state,
  logo,
  title,
  detail,
  detailLines = 1,
  detailIcon,
  dot,
  actions,
  actionsPinned = false,
  actionsBelow = false,
  menuItems,
  onBodyClick,
  onHoverChange,
  onMenuOpenChange,
  embedded = false,
}: ToastPillProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuOpenRef = useRef(false);
  // The ⋮ menu renders in a portal, so the pointer can leave the pill through
  // it without a mouse-leave; release the hover when it closes outside.
  const handleMenuOpenChange = (open: boolean) => {
    menuOpenRef.current = open;
    onMenuOpenChange?.(open);
    if (open) onHoverChange?.(true);
    if (!open && !rootRef.current?.matches(":hover")) onHoverChange?.(false);
  };
  const belowActions = actionsBelow
    ? actions.filter((action) => !action.icon)
    : [];
  const rowActions = actionsBelow
    ? actions.filter((action) => action.icon)
    : actions;
  const actionButtons =
    rowActions.length > 0 || menuItems.length > 0 ? (
      <div className="flex items-center gap-1" data-testid="toast-pill-actions">
        {rowActions.map((action) => (
          <ToastActionButton key={action.id} action={action} />
        ))}
        {menuItems.length > 0 && (
          <ToastMoreMenu
            items={menuItems}
            testId="toast-pill-menu"
            onOpenChange={handleMenuOpenChange}
            tray={embedded}
          />
        )}
      </div>
    ) : null;
  const DetailIcon = detailIcon ? Icons[detailIcon] : null;
  const twoLines = detailLines === 2 && !actionsBelow;
  const pinned = actionsPinned || actionsBelow;
  return (
    <div
      ref={rootRef}
      className={cn(
        !embedded && toastShellClassName,
        "relative shrink-0",
        embedded ? "w-full" : "denker-toast-width",
      )}
      data-testid="toast-pill"
      data-interactive="true"
      data-toast-key={toastKey}
      data-toast-state={state}
      {...(embedded ? {} : toastShellAttributes)}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => {
        if (!menuOpenRef.current) onHoverChange?.(false);
      }}
    >
      {/* Two lines of detail: the row wraps, its title still one line. */}
      <PocketRowWrapScope wrap={twoLines}>
        <PocketRow
          testId="toast-pill-body"
          className={
            embedded
              ? undefined
              : cn(
                  "min-h-[44px] pl-[9px] pr-3",
                  // Inside the shell's 24px corner, less its border.
                  actionsBelow ? "rounded-[23px]" : "rounded-full",
                  twoLines || actionsBelow ? "py-1.5" : "py-0",
                )
          }
          leading={logo}
          title={
            twoLines ? <span className="block truncate">{title}</span> : title
          }
          secondLine={
            <span
              className="flex min-w-0 items-center gap-1"
              data-testid="toast-pill-detail"
            >
              {DetailIcon && (
                <DetailIcon
                  className="h-3 w-3 shrink-0"
                  aria-hidden="true"
                  data-testid="toast-pill-tool-icon"
                />
              )}
              <span className={twoLines ? "line-clamp-2" : "truncate"}>
                {detail}
              </span>
            </span>
          }
          status={dot ? { tone: dot } : undefined}
          below={
            belowActions.length > 0 ? (
              <div
                className="flex items-center gap-1.5"
                data-testid="toast-pill-below-actions"
              >
                {belowActions.map((action) => (
                  <ToastActionButton key={action.id} action={action} />
                ))}
              </div>
            ) : undefined
          }
          trailing={pinned ? actionButtons : undefined}
          hoverActions={pinned ? undefined : actionButtons}
          onSelect={onBodyClick}
        />
      </PocketRowWrapScope>
    </div>
  );
}
